/**
 * 플랜 업그레이드 유도 모달
 * 429 쿼터 초과 또는 잠긴 기능 접근 시 표시
 */
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FiZap, FiX, FiArrowRight } from 'react-icons/fi'
import { cn } from '../../lib/utils'

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  feature?: string
  /** 쿼터 초과 정보 (429 응답) */
  quotaInfo?: {
    limit: number
    used: number
    reset_at?: string
    upgrade_url?: string
  }
  title?: string
  description?: string
}

export default function UpgradeModal({
  isOpen,
  onClose,
  feature,
  quotaInfo,
  title,
  description,
}: UpgradeModalProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  if (!isOpen) return null

  const handleUpgrade = () => {
    onClose()
    if (quotaInfo?.upgrade_url) {
      window.location.href = quotaInfo.upgrade_url
    } else {
      navigate('/dashboard/billing')
    }
  }

  const defaultTitle = feature
    ? t('upgradeModal.defaultTitleFeature', { feature })
    : quotaInfo
    ? t('upgradeModal.defaultTitleQuota')
    : t('upgradeModal.defaultTitleGeneric')

  const defaultDesc = quotaInfo
    ? t('upgradeModal.defaultDescQuota', { used: quotaInfo.used, limit: quotaInfo.limit })
    : description ?? t('upgradeModal.defaultDescGeneric')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 모달 패널 */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className={cn(
          'relative w-full max-w-md',
          'bg-surface-card border border-surface-elevated/60 rounded-lg shadow-md',
          'overflow-hidden',
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
      >
        {/* 상단 그라디언트 배너 */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary h-1" />

        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-ink-faint hover:text-ink transition-colors"
          aria-label={t('upgradeModal.closeAria')}
        >
          <FiX size={20} />
        </button>

        <div className="p-8">
          {/* 아이콘 */}
          <div className="flex justify-center mb-5">
            <div className="p-4 rounded-full bg-brand-primary/10 border border-brand-primary/20">
              <FiZap className="text-brand-primary-light" size={28} />
            </div>
          </div>

          {/* 제목 */}
          <h2
            id="upgrade-modal-title"
            className="text-xl font-bold text-center text-ink mb-3"
          >
            {title ?? defaultTitle}
          </h2>

          {/* 설명 */}
          <p className="text-sm text-ink-muted text-center leading-relaxed mb-6">
            {defaultDesc}
          </p>

          {/* 쿼터 정보 */}
          {quotaInfo && (
            <div className="bg-surface-elevated/30 rounded-md p-4 mb-6 border border-surface-elevated/40">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-ink-muted">{t('upgradeModal.usageLabel')}</span>
                <span className="text-ink font-medium">
                  {quotaInfo.used} / {quotaInfo.limit}
                </span>
              </div>
              <div className="h-2 bg-surface-elevated rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-red-400 rounded-full"
                  style={{ width: `${Math.min(100, (quotaInfo.used / quotaInfo.limit) * 100)}%` }}
                />
              </div>
              {quotaInfo.reset_at && (
                <p className="text-xs text-ink-faint mt-2 text-right">
                  {t('upgradeModal.resetLabel', {
                    date: new Date(quotaInfo.reset_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ko-KR'),
                  })}
                </p>
              )}
            </div>
          )}

          {/* 플랜 하이라이트 */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { plan: 'Starter', price: '₩4,900', color: 'text-green-400' },
              { plan: 'Pro', price: '₩30,000', color: 'text-brand-primary-light', highlight: true },
              { plan: 'Business', price: '₩90,000', color: 'text-brand-secondary' },
            ].map(({ plan, price, color, highlight }) => (
              <div
                key={plan}
                className={cn(
                  'rounded-md p-3 text-center border',
                  highlight
                    ? 'bg-brand-primary/10 border-brand-primary/40'
                    : 'bg-surface-elevated/20 border-surface-elevated/30',
                )}
              >
                <p className={cn('text-xs font-bold', color)}>{plan}</p>
                <p className="text-xs text-ink-muted mt-0.5">{t('upgradeModal.perMonth', { price })}</p>
              </div>
            ))}
          </div>

          {/* 액션 버튼 */}
          <div className="flex flex-col gap-2">
            <button
              onClick={handleUpgrade}
              className={cn(
                'w-full py-3 rounded-md font-semibold text-sm',
                'bg-gradient-to-r from-brand-primary to-brand-secondary text-white',
                'hover:opacity-90 transition-opacity',
                'flex items-center justify-center gap-2',
              )}
            >
              {t('upgradeModal.upgradeCta')}
              <FiArrowRight size={16} />
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-md text-sm text-ink-muted hover:text-ink transition-colors"
            >
              {t('upgradeModal.laterCta')}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
